/**
 * PractitionerConsentCard — Container card per practitioner.
 *
 * Active: fully interactive toggles.
 * Pending: dimmed (opacity 0.7), toggles disabled, banner shown.
 */

import React, { useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { GlassCard } from '../../glass';
import { PractitionerCardHeader } from './PractitionerCardHeader';
import { PendingPractitionerBanner } from './PendingPractitionerBanner';
import { ConsentToggleGroup } from './ConsentToggleGroup';
import { RevokeAccessButton } from './RevokeAccessButton';
import type {
  PractitionerConsentRecord,
  ConsentField,
} from '@/hooks/usePractitionerConsent';

interface PractitionerConsentCardProps {
  record: PractitionerConsentRecord;
  onConsentChange: (field: ConsentField, value: boolean) => Promise<void>;
  onRevoke: () => void;
  isSaving?: boolean;
}

export const PractitionerConsentCard: React.FC<PractitionerConsentCardProps> = React.memo(
  function PractitionerConsentCard({ record, onConsentChange, onRevoke, isSaving = false }) {
    const isPending = record.status === 'pending';

    const handleToggle = useCallback(
      (field: ConsentField, value: boolean) => onConsentChange(field, value),
      [onConsentChange]
    );

    return (
      <GlassCard
        withChromeBorder={false}
        noPadding
        style={isPending ? styles.pendingCard : undefined}
      >
        <View style={styles.inner}>
          <PractitionerCardHeader
            initials={record.practitionerInitials}
            name={record.practitionerName}
            role={record.practitionerRole}
            status={record.status}
            joinedAt={record.invitedAt}
            gradientIndex={record.gradientIndex}
          />

          {isPending && (
            <PendingPractitionerBanner practitionerName={record.practitionerName} />
          )}

          <ConsentToggleGroup
            consent={record.consent}
            onConsentChange={handleToggle}
            disabled={isPending}
            isSaving={isSaving}
          />

          <RevokeAccessButton
            status={record.status}
            onPress={onRevoke}
            requireConfirmation
          />
        </View>
      </GlassCard>
    );
  }
);

const styles = StyleSheet.create({
  pendingCard: {
    opacity: 0.7,
  },
  inner: {
    // Overflow hidden handled by GlassCard
  },
});
